import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useServices } from '../app/services';
import { getMeta, setMeta } from '../data/appMeta';
import { NO_ENTITLEMENT, type Entitlement, type PremiumFeature } from './entitlement';

interface PremiumValue extends Entitlement {
  /** The first answer has come (until then, nothing is shown as locked or open by mistake). */
  ready: boolean;
  /** Had Premium on this device and has it no more (it ended, or was cancelled and ran out). */
  lapsed: boolean;
  /** Whether this device may use a Premium feature now. */
  has(feature: PremiumFeature): boolean;
  /** Ask again (after a purchase, a restore, or signing in). */
  refresh(): Promise<Entitlement>;
}

const Ctx = createContext<PremiumValue | null>(null);

/** Set once this device has seen Premium on: its ending is then said (see `lapsed`). */
export const HAD_PREMIUM_KEY = 'premium.hadIt';

/**
 * Whether this person has Premium: asked at start, when the app comes back
 * to the foreground, when the store reports a change, and on refresh().
 */
export function PremiumProvider({ children }: { children: ReactNode }) {
  const { premium, identity, db } = useServices();
  const [state, setState] = useState<{ e: Entitlement; ready: boolean }>({ e: NO_ENTITLEMENT, ready: false });
  const [hadIt, setHadIt] = useState(false);
  useEffect(() => {
    void getMeta(db, HAD_PREMIUM_KEY).then((v) => v === '1' && setHadIt(true));
  }, [db]);
  useEffect(() => {
    if (!state.e.active) return;
    setHadIt(true);
    void setMeta(db, HAD_PREMIUM_KEY, '1');
  }, [state.e.active, db]);
  const userId = identity.session.userId;
  const latest = useRef(0);

  const refresh = useCallback(async () => {
    const ask = ++latest.current;
    const e = await premium.check(userId);
    // An older answer arriving late never overwrites a newer one.
    if (ask === latest.current) setState({ e, ready: true });
    return e;
  }, [premium, userId]);

  useEffect(() => {
    let unsubscribe = () => {};
    let live = true;
    void refresh().then(() => {
      if (live && premium.subscribe) unsubscribe = premium.subscribe((e) => setState({ e, ready: true }));
    });
    const onVisible = () => document.visibilityState === 'visible' && void refresh();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      live = false;
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [premium, refresh]);

  const value = useMemo<PremiumValue>(
    () => ({ ...state.e, ready: state.ready, lapsed: state.ready && !state.e.active && hadIt, has: () => state.e.active, refresh }),
    [state, refresh, hadIt],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePremium(): PremiumValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('usePremium outside PremiumProvider');
  return v;
}
